import React, { useState } from "react";
import { useT } from "@/lib/i18n";

const ROW_H = 28; // px; fijo, para dibujar solo las filas visibles aunque el archivo tenga miles
const VIEW_H = 288; // alto máximo de la ventana de revisión
const EXTRA = 12; // filas de más arriba y abajo para que el desplazamiento no parpadee

/**
 * Todas las filas del archivo cargado, en una ventana con desplazamiento, para el último chequeo antes de imprimir.
 * Dibuja solo las visibles (un CSV de 20 000 filas se desplaza igual de fluido que uno de 20).
 * La columna de cantidad se resalta y la cabecera queda fija al desplazarse.
 */
export default function BatchRowsTable({ columns, rows, quantityColumn, testId = "batch-rows" }) {
    const t = useT();
    const [scrollTop, setScrollTop] = useState(0);
    const total = rows.length;
    const start = Math.max(0, Math.floor(scrollTop / ROW_H) - EXTRA);
    const end = Math.min(total, Math.ceil((scrollTop + VIEW_H) / ROW_H) + EXTRA);
    const span = columns.length + 1;
    const spacer = (n, key) =>
        n > 0 ? (
            <tr key={key} aria-hidden="true" style={{ height: n * ROW_H }}>
                <td colSpan={span} />
            </tr>
        ) : null;

    return (
        <div translate="no">
            <label className="text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1.5 block">
                {t("batch.rows.title", { count: t("batch.rows.count", { n: total }) })}
            </label>
            <div
                data-testid={testId}
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
                className="border border-brand-200 overflow-auto"
                style={{ maxHeight: VIEW_H }}
            >
                <table className="w-full text-xs font-mono">
                    <thead className="bg-brand-100 text-brand-900 sticky top-0 z-10">
                        <tr>
                            <th className="px-2 py-1.5 text-right border-r border-brand-200 w-12">#</th>
                            {columns.map((c) => (
                                <th
                                    key={c}
                                    className={`px-2 py-1.5 text-left border-r border-brand-200 last:border-r-0 whitespace-nowrap ${
                                        c === quantityColumn ? "bg-green-100" : ""
                                    }`}
                                >
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {spacer(start, "top")}
                        {rows.slice(start, end).map((row, i) => (
                            <tr key={start + i} data-testid={`${testId}-row`} style={{ height: ROW_H }} className="border-t border-brand-200">
                                <td className="px-2 text-right text-brand-600 border-r border-brand-200">{start + i + 1}</td>
                                {columns.map((c) => {
                                    const value = String(row[c] ?? "");
                                    return (
                                        <td
                                            key={c}
                                            title={value}
                                            className={`px-2 border-r border-brand-200 last:border-r-0 text-brand-950 whitespace-nowrap overflow-hidden text-ellipsis max-w-[16rem] ${
                                                c === quantityColumn ? "bg-green-50 font-semibold" : ""
                                            }`}
                                        >
                                            {value}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {spacer(total - end, "bottom")}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
